import { useState, useEffect, useCallback, useRef } from 'react';
import { useSocket } from '@contexts/SocketContext';
import apiService from '@services/apiClient';

interface AssessmentChatRoom {
  roomId: number;
  assessmentId: number;
  assessmentName: string;
  contractName?: string;
  contractNumber?: string;
  supplierName?: string;
  alreadyExists?: boolean;
}

interface UseAssessmentChatResult {
  chatRoom: AssessmentChatRoom | null;
  isLoading: boolean;
  error: string | null;
  ensureChatRoom: () => Promise<void>;
  hasChatRoom: boolean;
}

/**
 * Hook to automatically ensure a chat room exists for an assessment
 * Provides methods to get or create chat rooms on-demand
 */
export const useAssessmentChat = (assessmentId: number | string | undefined): UseAssessmentChatResult => {
  const { isConnected, refreshChatRooms } = useSocket();
  const [chatRoom, setChatRoom] = useState<AssessmentChatRoom | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Track which assessment ID we've fetched to prevent duplicate requests
  const fetchedAssessmentIdRef = useRef<number | string | null>(null);

  // Function to get or create chat room
  const ensureChatRoom = useCallback(async () => {
    if (!assessmentId) {
      setError('Assessment ID is required');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Call the get-or-create endpoint using apiService (which handles MSAL token automatically)
      const data = await apiService.get<{ success: boolean; data: AssessmentChatRoom; error?: string }>(
        `/chat/assessments/${assessmentId}/room`
      );
      
      if (data.success) {
        setChatRoom(data.data);
        fetchedAssessmentIdRef.current = assessmentId;
        console.log(
          data.data.alreadyExists 
            ? `✓ Found existing chat room ${data.data.roomId} for assessment ${assessmentId}`
            : `🎉 Created new chat room ${data.data.roomId} for assessment ${assessmentId}`
        );
        
        // Always refresh chat rooms to ensure user has access (especially for admin users)
        refreshChatRooms().then(() => {
          // Small delay to ensure the refresh completes before auto-switching
          setTimeout(() => {
            console.log(`🔄 Chat rooms refreshed for assessment ${assessmentId}`);
          }, 300);
        });
      } else {
        throw new Error(data.error || 'Failed to get or create chat room');
      }
    } catch (err: unknown) {
      const error = err as Error;
      console.error('Error ensuring chat room:', err);
      setError(error.message || 'Failed to ensure chat room exists');
    } finally {
      setIsLoading(false);
    }
  }, [assessmentId, refreshChatRooms]);

  // Reset state when assessment ID changes
  useEffect(() => {
    if (assessmentId && fetchedAssessmentIdRef.current !== assessmentId) {
      setChatRoom(null);
      setError(null);
      fetchedAssessmentIdRef.current = null;
    }
  }, [assessmentId]);

  // Auto-create chat room when conditions are met
  useEffect(() => {
    const shouldFetch = 
      assessmentId && 
      isConnected && 
      !chatRoom && 
      !isLoading && 
      !error &&
      fetchedAssessmentIdRef.current !== assessmentId;
    
    if (shouldFetch) {
      ensureChatRoom();
    }
  }, [assessmentId, isConnected, chatRoom, isLoading, error, ensureChatRoom]);

  return {
    chatRoom,
    isLoading,
    error,
    ensureChatRoom,
    hasChatRoom: !!chatRoom
  };
};

export default useAssessmentChat; 